/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable prefer-arrow/prefer-arrow-functions */
/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient, HttpHeaders, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { mapToHttpParams } from '@bcl/design-system';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments';

@Injectable({
  providedIn: 'root',
})
export class AttachmentsService {
  iconMimeMap: { [key: string]: string[] } = {
    'fa-file-image.svg': [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/bmp',
      'image/svg+xml',
      'image/webp',
      'image/tiff',
    ],
    'fa-file-pdf.svg': ['application/pdf'],
    'fa-file-audio.svg': ['audio/mpeg', 'audio/ogg', 'audio/wav', 'audio/wma'],
    'fa-file-video.svg': [
      'video/mp4',
      'video/ogg',
      'video/webm',
      'video/x-msvideo',
      'video/mpeg',
    ],
    'fa-file-compressed.svg': [
      'application/zip',
      '	application/x-7z-compressed',
      'application/vnd.rar',
      'application/x-tar',
    ],
    'fa-file-excel.svg': [
      'application/excel',
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    'fa-file-document.svg': [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/rtf',
      'text/plain',
    ],
    'fa-file-presentation.svg': [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/mspowerpoint',
    ],
  };
  private refreshDataSubject = new Subject<void>();
  private refreshData$ = this.refreshDataSubject.asObservable();
  constructor(private http: HttpClient) {}

  public saveFile(
    objectType: string,
    objectId: number | string,
    file: File
  ): Observable<any> {
    const url = `${environment.apiHost}/files/${objectType}/${objectId}`;
    const formData = new FormData();
    formData.append('file', file, file.name);

    const boundary =
      '----WebKitFormBoundary' + Math.random().toString(36).substring(2);
    return this.http.post(url, formData, {
      headers: {
        'Content-Type': `multipart/form-data; boundary=${boundary}`,
      },
      reportProgress: true,
      observe: 'events',
    });
  }

  public getAttachments(filters: any): Observable<any> {
    const url = `${environment.apiHost}/files`;
    const params = mapToHttpParams({
      dateFilter: filters?.dateFilter ? filters.dateFilter : '4',
      type: filters?.typeFilter ? filters.typeFilter : null,
      parentObject: filters?.parentObject ? filters.parentObject : 'CRM.Leads',
      parentID: filters?.parentID ? filters.parentID : null,
      searchText: filters?.searchText ? filters.searchText : '',
      pageNumber: filters?.pageNumber ? filters.pageNumber : 1,
      pageSize: filters?.pageSize ? filters.pageSize : 10,
    });

    return this.http.get<any>(url, { params });
  }

  public newAttachment(newAttachment: AddedAttachment): Observable<any> {
    const pAttachment = {
      parentObject: newAttachment.parentObject,
      parentID: newAttachment.parentID,
      fileName: newAttachment.fileName,
      description: newAttachment.description,
      size: newAttachment.size,
      createdBy: newAttachment.createdBy,
      mimeType: newAttachment.mimeType,
      referenceId: newAttachment.referenceId,
      type: newAttachment.type,
      customerShare: newAttachment.customerShare,
      restricted: newAttachment.restricted,
    };
    return this.http.post<any>(
      `${environment.apiHost}/files/submit`,
      pAttachment
    );
  }

  public updateAttachment(
    updatedAttachment: EditedAttachment
  ): Observable<any> {
    return this.http.patch<any>(
      `${environment.apiHost}/files`,
      updatedAttachment
    );
  }

  public deleteAttachment(
    referenceId: string,
    id: number | null
  ): Observable<any> {
    const params = {
      id,
      referenceId,
    };

    return this.http.delete<any>(`${environment.apiHost}/files`, {
      body: params,
    });
  }

  public downloadFile(id: string): Observable<any> {
    const headers = new HttpHeaders({
      responseType: 'blob',
    });
    const url = `${environment.apiHost}/files/${id}`;

    return this.http.get<any>(url, {
      headers,
      responseType: 'blob' as 'json',
    });
  }

  public getIconClass(mimeType: string): string {
    for (const [icon, types] of Object.entries(this.iconMimeMap)) {
      if (types.includes(mimeType)) {
        return icon;
      }
    }
    return 'fa-file-other.svg';
  }

  public formatBytes(bytes: number) {
    const decimals = 1;
    if (!+bytes) {
      return '0 Bytes';
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'kB', 'MB', 'GB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }
  refreshAttachments() {
    this.refreshDataSubject.next();
  }

  public getFileUrl(id: string): string {
    return `${environment.apiHost}/files/${id}`;
  }
}
export type Attachments = {
  TotalEntries: number;
  Attachments: Attachment[];
};
export type Attachment = {
  ID: number;
  FileName: string;
  Description: string;
  Size: number;
  CreatedBy: string;
  CreatedDate: string;
  MimeType: string;
  ReferenceID: string;
  Type: string;
  CustomerShare: boolean;
  Restricted: boolean;
  ParentObject: string;
  ParentID: number;
};

export type AttachmentsList = Readonly<{
  attachments: Attachment[];
  totalEntries: number;
}>;

type AuthorDate = {
  at: Date;
  by: string;
};

export type AttachmentMain = Readonly<{
  id: number;
  fileName?: string;
  size?: number;
  createdBy?: string;
  updatedBy?: string;
  createdByName?: string;
  updatedByName?: string;
  dateAdded?: Date;
  dateUpdated?: Date;
  description?: string;
  mimeType?: string;
  referenceId?: string;
  parentObject?: string;
  parentID?: string;
  type?: number;
  customerShare?: boolean;
  restricted?: boolean;
}>;

export type AddedAttachment = Pick<
  AttachmentMain,
  | 'fileName'
  | 'description'
  | 'size'
  | 'createdBy'
  | 'mimeType'
  | 'referenceId'
  | 'parentObject'
  | 'parentID'
  | 'type'
  | 'customerShare'
  | 'restricted'
>;
export type EditedAttachment = Pick<
  AttachmentMain,
  | 'id'
  | 'fileName'
  | 'description'
  | 'updatedBy'
  | 'parentObject'
  | 'parentID'
  | 'type'
  | 'customerShare'
  | 'restricted'
>;

export function isAttachment(
  Attachment: Attachment | AddedAttachment
): Attachment is Attachment {
  return (<AttachmentMain>Attachment).id !== undefined;
}

export type AttachmentsListFilter = Readonly<{
  date: string | null;
  type: string | null;
  search: string | null;
}>;
export type AttachmentsListPageChanged = Readonly<{
  pageNumber: number;
  pageSize: number;
  filter: Partial<AttachmentsListFilter>;
}>;
export enum DateFilterType {
  ThisMonth = 0,
  LastWeek = 1,
  LastMonth = 2,
  Older = 3,
}
